import { useEffect, useState } from "react";

const generateKeyValue = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

interface UsePushParams {
  publicKey: string;
}

export interface Subscription {
  endpoint: string;
  auth_key?: string;
  p256dh_key?: string;
  user_agent: string;
}
const extractSubscription = (ps: PushSubscription): Subscription => {
  const json = ps.toJSON();
  return {
    endpoint: ps.endpoint,
    auth_key: json.keys?.auth,
    p256dh_key: json.keys?.p256dh,
    user_agent: navigator.userAgent,
  };
};

interface PushState {
  pushManager?: PushManager;
  permissionState?: PermissionState;
  subscription?: Subscription;
}
interface UsePushReturn {
  serviceWorkersAvailable: boolean;
  permissionState?: PermissionState;
  pushSubscription?: Subscription;
  askForPermission: () => void;
}
export function usePush({ publicKey }: UsePushParams): UsePushReturn {
  const [state, setState] = useState<PushState>();
  const serviceWorkersAvailable = Boolean(navigator.serviceWorker);

  useEffect(() => {
    if (!serviceWorkersAvailable) return;

    const initializePushState = async () => {
      await navigator.serviceWorker.register("/serviceworker.js", {
        scope: "/",
      });
      const { pushManager } = await navigator.serviceWorker.ready;
      const permissionState = await pushManager.permissionState({
        userVisibleOnly: true,
      });
      const subscription = await pushManager.getSubscription();

      setState({
        pushManager: pushManager,
        permissionState: permissionState,
        subscription: subscription
          ? extractSubscription(subscription)
          : undefined,
      });
    };

    initializePushState().catch(console.error);
  }, [publicKey]);

  const askForPermission = async () => {
    const key = generateKeyValue(publicKey);
    try {
      const subscription = await state?.pushManager?.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
      });
      const permissionState = await state?.pushManager?.permissionState({
        userVisibleOnly: true,
      });
      setState((state) => ({
        ...state,
        permissionState,
        subscription: subscription
          ? extractSubscription(subscription)
          : undefined,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  return {
    serviceWorkersAvailable,
    permissionState: state?.permissionState,
    pushSubscription: state?.subscription,
    askForPermission,
  };
}
